import { css } from '@emotion/react';
import styled from '@emotion/styled';
import tokens from '@vaad1st/shared.design-tokens/json/variables.json';
import { NoGapSupport } from '../../lib/utils/mixins';
import { BreakPointToken } from '@vaad1st/hook.media-query';

export interface InputWidths {
	desktop: string;
	tablet: string;
}

export const mobileGridGap = tokens.spacing_8px.value;

export const tabletGridGaps = {
	horizontal: tokens.spacing_12px.value,
	vertical: tokens.spacing_24px.value - 10
};

export const desktopGridGaps = {
	horizontal: tokens.spacing_8px.value,
	vertical: tokens.spacing_24px.value - 10
};

export const submitBtnWidthDesktop = 177;

export const ServerError = styled.div`
	margin-bottom: ${tokens.spacing_16px.value}px;
`;

export const VisuallyHidden = styled.span`
	position: absolute;
	width: 1px;
	height: 1px;
	margin: -1px;
	padding: 0;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	clip-path: inset(50%);
	white-space: nowrap;
	border: 0;
`;

export const FormGrid = styled.section`
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;
	gap: ${mobileGridGap}px;

	@media (min-width: ${BreakPointToken.MdMin}px) {
		column-gap: ${tabletGridGaps.horizontal}px;
		row-gap: ${tabletGridGaps.vertical}px;
	}

	@media (min-width: ${BreakPointToken.LgMin}px) {
		column-gap: ${desktopGridGaps.horizontal}px;
		row-gap: ${desktopGridGaps.vertical}px;
	}
`;

export const column = css`
	${NoGapSupport(
		() => css`
			margin-top: ${mobileGridGap}px;

			@media (min-width: ${BreakPointToken.MdMin}px) {
				margin: 0;
			}
		`
	)}
`;

export const option = css`
	width: max-content;

	${NoGapSupport(
		() => css`
			margin-top: ${tokens.spacing_20px.value}px;

			@media (min-width: ${BreakPointToken.MdMin}px) {
				margin-top: 0;
			}
		`
	)}
`;

export const rewardText = css`
	@media (min-width: ${BreakPointToken.MdMin}px) {
		margin-inline-end: auto;
		margin-inline-start: 4px;
		padding-inline-start: ${tokens.spacing_12px.value}px;
		border-inline-start: 1px solid var(--color-border-neutral-minimal);
	}
`;

export const rewardStatus = css`
	&:empty {
		position: absolute;
		width: 1px;
		height: 1px;
		margin: -1px;
		padding: 0;
		overflow: hidden;
		clip: rect(0, 0, 0, 0);
		white-space: nowrap;
		border: 0;
	}
`;

export const half = css`
	flex: 0 0 calc(50% - ${mobileGridGap / 2}px);

	@media (min-width: ${BreakPointToken.MdMin}px) {
		flex: unset;
	}

	${NoGapSupport(
		() => css`
			& + & {
				margin-left: ${mobileGridGap}px;
				flex-shrink: 0;
				flex-grow: 0;
			}

			@media (min-width: ${BreakPointToken.MdMin}px) {
				flex: 0 0 auto;
				margin-right: ${tabletGridGaps.horizontal}px;

				& + & {
					margin-left: 0;
				}
			}
		`
	)}
`;

export const FormOptions = styled.section`
	display: contents;
	flex: 0 0 100%;

	@media (min-width: ${BreakPointToken.MdMin}px) {
		display: flex;
		gap: ${tokens.spacing_8px.value}px;
		justify-content: space-between;
		align-items: center;
	}
`;

export const origin = (inputWidths: InputWidths) => css`
	@media (min-width: ${BreakPointToken.LgMin}px) {
		flex: 0 0 ${inputWidths.desktop};
	}
`;

export const destination = (inputWidths: InputWidths) => css`
	@media (min-width: ${BreakPointToken.LgMin}px) {
		flex: 0 0 ${inputWidths.desktop};
	}
`;

export const date = (inputWidths: InputWidths) => css`
	@media (min-width: ${BreakPointToken.MdMin}px) {
		flex: 0 0 ${inputWidths.tablet};
	}

	@media (min-width: ${BreakPointToken.LgMin}px) {
		flex: 0 0 ${inputWidths.desktop};
	}

	input {
		cursor: pointer;
	}
`;

export const passengers = (inputWidths: InputWidths) => css`
	@media (min-width: ${BreakPointToken.MdMin}px) {
		flex: 0 0 ${inputWidths.tablet};
	}

	@media (min-width: ${BreakPointToken.LgMin}px) {
		flex: 0 0 ${inputWidths.desktop};
	}

	input {
		cursor: pointer;
	}
`;

export const hotelInput = (inputWidths: InputWidths) => css`
	@media (min-width: ${BreakPointToken.MdMin}px) {
		flex: 0 0 ${inputWidths.tablet};
	}

	@media (min-width: ${BreakPointToken.LgMin}px) {
		flex: 0 0 ${inputWidths.desktop};
	}

	input {
		cursor: pointer;
	}
`;

export const submit = (inputWidths: InputWidths) => css`
	order: 5;
	margin-left: auto;

	@media (min-width: ${BreakPointToken.MdMin}px) {
		flex: 0 0 ${inputWidths.tablet};
		order: unset;
	}

	@media (min-width: ${BreakPointToken.LgMin}px) {
		margin-top: 10px; // specific value to offset for input labels
		flex: 0 0 ${submitBtnWidthDesktop}px;
	}

	${NoGapSupport(
		() => css`
			@media (min-width: ${BreakPointToken.MdMin}px) {
				margin-left: ${tabletGridGaps.horizontal}px;
			}
		`
	)}
`;

export const discount = css`
	order: 6;
	flex: 0 0 100%;
	margin: ${tokens.spacing_12px.value}px 0;
	@media (min-width: ${BreakPointToken.MdMin}px) and (max-width: ${BreakPointToken.MdMax}px) {
		order: 8;
		flex: auto;
		justify-content: flex-end;
		margin: 0;
	}
	@media (min-width: ${BreakPointToken.LgMin}px) {
		order: 8;
		flex: 0 0 max-content;
		margin: 0;
	}
	text-align: center;
	display: flex;
	align-items: center;
	justify-content: center;
`;

// Shamefully changing the styles of readOnly inputs for Search XP
export const readOnly = css`
	& input {
		cursor: pointer;
	}

	& input[readonly] {
		&:not([disabled]) {
			background-color: ${tokens.color_light_background_neutral_primary} !important;

			& ~ label {
				background: ${tokens.color_light_background_neutral_primary} !important;
			}

			& ~ div {
				--icon-color: ${tokens.color_light_text_neutral_primary} !important;
			}

			&[class*='module_error'] {
				border: 1px solid ${tokens.color_light_text_semantic_error} !important;

				&:hover,
				&:active {
					border: 1px solid ${tokens.color_light_text_semantic_error} !important;
				}
			}
		}
	}
`;

export const Div = styled.div``;

export const LinkPanelGrid = styled.div`
	display: flex;
	flex-wrap: wrap;
`;

export const LinkPanelGridContent = styled.div`
	flex: 0 0 100%;
	@media (min-width: ${BreakPointToken.MdMin}px) {
		flex: 1;
	}
`;

export const LinkPanelGridAction = styled.div`
	flex: 0 0 100%;
	margin-top: ${tokens.spacing_16px.value}px;
	@media (min-width: ${BreakPointToken.MdMin}px) {
		margin-left: auto;
		flex: unset;
		margin: unset;
	}
`;

export const BookingTypeMeta = styled.section`
	color: var(--color-text-neutral-primary);
	margin-bottom: ${tokens.spacing_16px.value}px;

	img {
		margin-bottom: ${tokens.spacing_16px.value}px;
	}

	a {
		color: var(--color-text-neutral-primary);
		text-decoration: underline;
	}
`;
